import {WorkforceTeamCriteriaState, WorkforceCriteriaApi} from './workforce-team-criteria.state';
import {WorkforceApi, WorkforceBrowseState, WorkforceEmployee} from './workforce-browse.state';
import {TeamCreationIntent} from './workforce-team-creation.state';
export interface MemberChoice {memberId: string; firstName: string | null; lastName: string | null; email: string | null; linked: boolean; reason: string | null; }
export interface EmployeeMembership {employeeKey: string; memberId: string | null; reason: string | null; }
export interface MemberSelectionPage {companyId: string; teamId: string; snapshotId: string; employees: EmployeeMembership[]; members: MemberChoice[]; nextMemberId: string | null; }
export interface MemberSelectionApi extends WorkforceApi, WorkforceCriteriaApi {
  getWorkforceMemberSelection(companyId: string, teamId: string, request: {snapshotId: string; employeeKeys: string[]; includeMembers?: boolean; afterMemberId?: string; search?: string}): Promise<MemberSelectionPage>;
}
type MemberEmployee = WorkforceEmployee;
export class WorkforceTeamMembersState extends WorkforceBrowseState {
  readonly criteria: WorkforceTeamCriteriaState;
  rulesEditing = false;
  addMatching = false;
  readonly removals = new Map<string, string>();
  members: MemberChoice[] = [];
  memberSearch = '';
  private appliedMemberSearch = '';
  membersLoading = false;
  membersError = '';
  nextMemberId: string | null = null;
  private memberCursor: string | null = null;
  private memberHistory: Array<string | null> = [];
  private memberVersion = 0;
  constructor(private memberApi: MemberSelectionApi, readonly teamId: string) {
    super({
      getWorkforceBrowseContext: companyId => memberApi.getWorkforceBrowseContext(companyId),
      getWorkforceFilterOptions: (companyId, query) => memberApi.getWorkforceFilterOptions(companyId, query),
      getWorkforceEmployees: async (companyId, query) => {
        const page = await memberApi.getWorkforceEmployees(companyId, query);
        if (page.mode !== 'snapshot' || page.availability !== 'ready' || !page.snapshot) { return page; }
        const mapped = await memberApi.getWorkforceMemberSelection(companyId, teamId, {
          snapshotId: page.snapshot.id, employeeKeys: page.employees.map(row => row.employeeKey), includeMembers: false,
        });
        if (mapped.companyId !== companyId || mapped.teamId !== teamId || mapped.snapshotId !== page.snapshot.id
          || mapped.employees.length !== page.employees.length) { throw new Error('Scope changed'); }
        return {...page, employees: page.employees.map(row => {
          const membership = mapped.employees.find(value => value.employeeKey === row.employeeKey);
          if (!membership) { throw new Error('Incomplete membership mapping'); }
          return {...row, membership};
        })};
      },
    });
    this.criteria = new WorkforceTeamCriteriaState(memberApi);
  }
  override clear(): void {
    super.clear(); this.criteria?.destroy(); this.rulesEditing = false; this.addMatching = false;
    this.resetMembers(); this.membersError = '';
  }
  protected override invalidateAccess(): void {
    // Keep this editor's input/baseline through failure and Retry; discard all execution evidence.
    super.clear(); this.onSnapshotChanged();
  }
  protected override onSnapshotChanged(): void {
    this.criteria.invalidateSnapshot();
    this.resetMembers(); this.membersError = 'Reload current members to review this snapshot.';
  }
  private resetMembers(): void {
    this.removals?.clear(); this.members = []; this.nextMemberId = null;
    this.memberCursor = null; this.memberHistory = []; ++this.memberVersion; this.membersLoading = false;
  }
  override canSelect(row: MemberEmployee): boolean {
    return !!row.membership && !row.membership.reason && (!!row.membership.memberId || super.canSelect(row));
  }
  override isSelected(row: MemberEmployee): boolean {
    return row.membership?.memberId ? !this.removals.has(row.membership.memberId) : this.selection.has(row.employeeKey);
  }
  override toggleSelection(row: MemberEmployee): void {
    if (!this.canSelect(row)) { return; }
    if (row.membership?.memberId) {
      this.toggleMember({memberId: row.membership.memberId, firstName: row.firstName, lastName: row.lastName,
        email: row.primaryEmail, linked: true, reason: row.membership.reason});
    } else {
      super.toggleSelection(row);
      if (!this.withinRequestBound()) { this.selection.delete(row.employeeKey); this.notice = 'This edit exceeds the request size limit.'; }
    }
  }
  override get unselectedPageRows(): WorkforceEmployee[] { return this.rows.filter(row => this.canSelect(row) && !this.isSelected(row)); }
  override get canSelectPage(): boolean {
    const keys = new Set(this.selection.keys());
    const removals = new Set(this.removals.keys());
    this.unselectedPageRows.forEach(row => {
      if (row.membership?.memberId) { removals.delete(row.membership.memberId); }
      else { keys.add(row.employeeKey); }
    });
    return !this.loading && this.unselectedPageRows.length > 0 && keys.size <= 100
      && new TextEncoder().encode(JSON.stringify({...this.intent(), employeeKeys: [...keys], removalMemberIds: [...removals]})).byteLength <= 65536;
  }
  override selectPage(): void {
    if (!this.canSelectPage) { return; }
    const rows = this.unselectedPageRows;
    // Restore pending removals first so byte checks see the final bounded delta.
    rows.filter(row => !!row.membership?.memberId).forEach(row => this.toggleSelection(row));
    rows.filter(row => !row.membership?.memberId).forEach(row => this.toggleSelection(row));
  }
  toggleMember(member: MemberChoice): void {
    if (member.reason || this.mode !== 'snapshot' || !this.context?.snapshot) { return; }
    ++this.selectionRevision;
    if (this.removals.has(member.memberId)) { this.removals.delete(member.memberId); return; }
    this.removals.set(member.memberId, `${member.firstName || ''} ${member.lastName || ''}`.trim() || member.email || 'Member');
    this.selectionSnapshotId = this.context.snapshot.id;
    if (!this.withinRequestBound()) { this.removals.delete(member.memberId); this.notice = 'This edit exceeds the request size limit.'; }
  }
  get rulesChanged(): boolean { return this.rulesEditing && this.criteria.dirty; }
  editRules(): void {
    if (!this.criteria.saved || this.criteria.loading) { return; }
    this.rulesEditing = true; this.criteria.field = this.criteria.criteria.rules[0]?.field || 'department';
    this.criteria.prefix = ''; if (!this.criteria.criteria.allEligible) { void this.criteria.loadOptions(); }
  }
  discardRuleChanges(): void {
    this.criteria.criteria = this.criteria.saved?.criteria ? JSON.parse(JSON.stringify(this.criteria.saved.criteria))
      : {version: 1, allEligible: false, rules: []};
    this.criteria.invalidate(); this.rulesEditing = false;
    if (!this.criteria.saved?.criteria) { this.addMatching = false; }
  }
  get hasChanges(): boolean { return !!((!this.addMatching && this.selection.size) || this.removals.size || this.rulesChanged || this.addMatching); }
  get canSave(): boolean {
    return this.mode === 'snapshot' && this.context?.availability === 'ready' && !this.loading && !this.membersLoading
      && (!(this.selection.size || this.removals.size) || this.selectionSnapshotId === this.context?.snapshot?.id)
      && (!(this.rulesChanged || this.addMatching) || !!this.criteria.saved && this.criteria.valid && this.criteria.ready && !this.criteria.loading
        && this.criteria.companyId === this.companyId && this.criteria.teamId === this.teamId
        && this.criteria.snapshotId === this.context?.snapshot?.id) && this.withinRequestBound();
  }
  intent(): TeamCreationIntent {
    return {editMembers: true, snapshotId: this.selectionSnapshotId || this.context?.snapshot?.id || '', teamId: this.teamId,
      employeeKeys: this.addMatching ? [] : [...this.selection.keys()], removalMemberIds: [...this.removals.keys()],
      ...(this.rulesChanged && this.criteria.saved ? {criteriaUpdate: {revision: this.criteria.saved.revision,
        criteria: JSON.parse(JSON.stringify(this.criteria.criteria))}} : {}),
      ...(this.addMatching && this.criteria.saved ? {selection: {mode: 'all_matching', revision: this.criteria.saved.revision,
        criteria: JSON.parse(JSON.stringify(this.criteria.criteria))}} : {})};
  }
  private withinRequestBound(): boolean { return new TextEncoder().encode(JSON.stringify(this.intent())).byteLength <= 65536; }
  get canGoBackMembers(): boolean { return this.memberHistory.length > 0; }
  async loadMembers(reset = false): Promise<void> {
    // Only Search applies the draft; paging and Retry retain the cursor's query.
    if (reset) { this.appliedMemberSearch = this.memberSearch; this.memberHistory = []; this.memberCursor = null; }
    const snapshotId = this.context?.snapshot?.id;
    if (this.mode !== 'snapshot' || !snapshotId) { return; }
    const version = ++this.memberVersion;
    this.membersLoading = true; this.membersError = ''; this.members = []; this.nextMemberId = null;
    try {
      const page = await this.memberApi.getWorkforceMemberSelection(this.companyId, this.teamId, {
        snapshotId, employeeKeys: [], search: this.appliedMemberSearch, ...(this.memberCursor ? {afterMemberId: this.memberCursor} : {}),
      });
      if (version !== this.memberVersion || snapshotId !== this.context?.snapshot?.id) { return; }
      if (page.companyId !== this.companyId || page.teamId !== this.teamId || page.snapshotId !== snapshotId || page.members.length > 50) { throw new Error('Scope changed'); }
      this.members = page.members; this.nextMemberId = page.nextMemberId;
    } catch (_) { if (version === this.memberVersion) { this.membersError = 'Unable to load current members. Retry or reopen this editor.'; } }
    finally { if (version === this.memberVersion) { this.membersLoading = false; } }
  }
  async nextMembers(): Promise<void> {
    if (this.membersLoading || !this.nextMemberId) { return; }
    this.memberHistory.push(this.memberCursor);
    if (this.memberHistory.length > this.historyLimit) { this.memberHistory.shift(); }
    this.memberCursor = this.nextMemberId; await this.loadMembers();
  }
  async previousMembers(): Promise<void> {
    if (this.membersLoading || !this.memberHistory.length) { return; }
    this.memberCursor = this.memberHistory.pop() || null; await this.loadMembers();
  }
}
